import React, { useState, useEffect, useCallback } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Trophy, RefreshCw, Brain, Zap, CheckCircle2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// --- SHARED CONSTANTS ---
const DIRECTIONS = [
  { id: 'UP', icon: ChevronUp, opposite: 'DOWN' },
  { id: 'DOWN', icon: ChevronDown, opposite: 'UP' },
  { id: 'LEFT', icon: ChevronLeft, opposite: 'RIGHT' },
  { id: 'RIGHT', icon: ChevronRight, opposite: 'LEFT' }
];

// --- CORE MANAGER (The Main Entry Point) ---
const NeuralManager = () => {
  const [activeGame, setActiveGame] = useState('MENU');
    const [loading, setLoading] = useState(0);
      const navigate = useNavigate();
    
  

  // Lockdown: Prevent any scrolling or system gestures on the body
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  // Render Logic
  return (
    <div className="fixed inset-0 bg-black text-white font-sans select-none touch-none z-[99999] overflow-hidden">
{activeGame === 'MENU' && (
  <div className="fixed inset-0 z-[100] bg-[#0f1115] text-[#e2e8f0] flex flex-col items-center justify-between overflow-hidden select-none px-6 py-10">
    
    {/* 1. SOFT BACKGROUND GRADIENT */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-white/5 blur-[120px] rounded-full" />
    </div>

    {/* 2. SIMPLE TOP NAV */}
    <div className="relative w-full max-w-4xl flex justify-between items-center z-20 shrink-0 border-b border-white/5 pb-6">
      <div className="flex flex-col gap-1">
        <p className="text-[10px] tracking-widest font-bold uppercase text-white">
          Omore Games
        </p>
        <div className="flex items-center gap-2">
           <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse" />
           <p className="text-[8px] text-zinc-200 uppercase">System Ready</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-[8px] text-zinc-200 uppercase mb-1">Loading Data</p>
        <div className="w-24 h-1 bg-zinc-800 rounded-full overflow-hidden">
          {/* Note: Ensure 'loading' variable exists in your state */}
          <div className="h-full bg-white transition-all duration-700" style={{ width: `${typeof loading !== 'undefined' ? loading : 100}%` }} />
        </div>
      </div>
    </div>

    {/* 3. MAIN CONTENT - Clean & Friendly */}
    <div className="relative z-10 w-full max-w-2xl flex flex-col flex-1 justify-center space-y-10">
      
      <header className="space-y-2">
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white">
          Choose Your <span className="text-blue-400">Task</span>
        </h1>
        <p className="text-sm md:text-base text-zinc-400 font-medium">
          Select a game below to start your training session.
        </p>
      </header>

      {/* GAME SELECTION CARDS WITH CORNER BORDERS */}
      <div className="flex flex-col gap-4 w-full">
        {[
{ id: "01", title: "Arrow Match", desc: "Swipe or tap in the right direction.", type: "TILT" },
{ id: "02", title: "Pattern Memory", desc: "Watch the pattern and repeat it.", type: "SEQUENCE" },
        //   { id: "03", title: "Quick Tap", desc: "Reaction speed synchronization", type: "schulte" },
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveGame(item.type)}
            className="group relative w-full text-left transition-all duration-300"
          >
            {/* CORNER ACCENTS */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-white/10 group-hover:border-blue-400 transition-colors duration-300 rounded-tl-2xl z-20" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-white/10 group-hover:border-blue-400 transition-colors duration-300 rounded-br-2xl z-20" />

            <div className="relative p-6 md:p-8 bg-[#1a1d23] border border-white/5 rounded-2xl flex items-center justify-between group-hover:bg-[#23272f] group-hover:border-blue-400/30 transition-all">
              <div className="flex items-center gap-6">
                <div className="flex items-center justify-center w-12 h-12 bg-[#0f1115] rounded-xl text-zinc-200 group-hover:text-blue-400 font-bold text-sm border border-white/5 transition-colors">
                  {item.id}
                </div>
                <div className="flex flex-col">
                  <h3 className="text-xl md:text-2xl font-bold text-white">
                    {item.title}
                  </h3>
                  <p className="text-xs md:text-sm text-zinc-200">
                    {item.desc}
                  </p>
                </div>
              </div>
              
              {/* Chevron/Arrow - Ensure Lucide or similar icon is available, or use CSS arrow */}
              <div className="opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300 text-blue-400">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14m-7-7 7 7-7 7"/></svg>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* QUIT BUTTON */}
      <button
         onClick={() => navigate("/game/start")}
        className="group flex items-center gap-3 text-sm text-zinc-200 hover:text-white transition-all w-fit bg-transparent border-none p-0 cursor-pointer"
      >
        <span className="font-bold tracking-wide">[ Exit Game ]</span>
        <div className="w-8 h-[1px] bg-zinc-800 group-hover:w-12 group-hover:bg-red-500 transition-all" />
      </button>
    </div>

    {/* 4. FOOTER */}
    <div className="relative w-full max-w-4xl flex justify-between opacity-40 shrink-0 text-[9px] font-bold text-zinc-200">
      <p>ESTABLISHED CONNECTION</p>
    </div>
  </div>
)}

      {activeGame === 'TILT' && <NeuralTilt onBack={() => setActiveGame('MENU')} />}
      {activeGame === 'SEQUENCE' && <NeuralSequence onBack={() => setActiveGame('MENU')} />}
    </div>
  );
};

// --- MODULE 1: DIRECTION LOCK ---
const NeuralTilt = ({ onBack }) => {
  const [task, setTask] = useState({ dir: DIRECTIONS[0], type: 'NORMAL' });
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(2.0);
  const [isPaused, setIsPaused] = useState(true);
  const [hasPlayed, setHasPlayed] = useState(false);

  const finishGame = useCallback(() => {
    setIsPaused(true);
    setHasPlayed(true);
  }, []);

  const generateTask = useCallback((currentScore) => {
    const randomDir = DIRECTIONS[Math.floor(Math.random() * DIRECTIONS.length)];
    const randomType = Math.random() > 0.5 ? 'NORMAL' : 'REVERSE';
    setTask({ dir: randomDir, type: randomType });
    setTimeLeft(Math.max(2.0 - (currentScore * 0.06), 0.6));
  }, []);

  useEffect(() => {
    let timer;
    if (!isPaused && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 0.05) { finishGame(); return 0; }
          return prev - 0.05;
        });
      }, 50);
    }
    return () => clearInterval(timer);
  }, [isPaused, timeLeft, finishGame]);

  const handleInput = (inputDir) => {
    if (isPaused) return;
    const isCorrect = task.type === 'NORMAL' ? inputDir === task.dir.id : inputDir === task.dir.opposite;
    if (isCorrect) {
      const ns = score + 1;
      setScore(ns);
      if (ns >= 20) finishGame();
      else generateTask(ns);
    } else finishGame();
  };

  return (
    <div className="h-full w-full flex flex-col items-center justify-between relative overflow-hidden">
      <div className={`absolute inset-0 transition-colors duration-500 ${task.type === 'NORMAL' ? 'bg-blue-950/20' : 'bg-red-950/100'}`} />
      
{isPaused && (
  <div 
    className="absolute inset-0 z-[100] bg-[#050505] flex flex-col items-center justify-between p-8 md:p-16 text-center select-none overflow-hidden" 
    onPointerDown={() => { setScore(0); generateTask(0); setIsPaused(false); }}
  >
    
    {/* Background Glow */}
    <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(circle_at_50%_50%,_#3b82f610_0%,_transparent_70%)]" />

    {!hasPlayed ? (
      <div className="relative z-10 flex flex-col items-center justify-center h-full w-full max-w-sm animate-in fade-in zoom-in duration-500">
        <h1 className="text-5xl md:text-7xl font-black  mb-8 uppercase tracking-tighter leading-none">
          Arrow <br/> <span className="text-blue-500">Match</span>
        </h1>

        <div className="w-full space-y-4 mb-10 text-left bg-white/[0.03] border border-white/5 p-6 rounded-3xl">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-200 mb-2">Instructions:</p>
          
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center shadow-[0_0_15px_rgba(59,130,246,0.4)]">
              <span className="text-white text-lg">↑</span>
            </div>
            <div>
              <p className="text-xs font-black uppercase text-white">Blue Arrow</p>
              <p className="text-[10px] text-zinc-200">Tap the SAME direction</p>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center shadow-[0_0_15px_rgba(220,38,38,0.4)]">
              <span className="text-white text-lg">↓</span>
            </div>
            <div>
              <p className="text-xs font-black uppercase text-white">Red Arrow</p>
              <p className="text-[10px] text-zinc-200">Tap the OPPOSITE direction</p>
            </div>
          </div>
        </div>

        <button className="w-full py-5 bg-white text-black rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-xl active:scale-95 transition-all">
          Start Game
        </button>
      </div>
    ) : (
      <div className="relative z-10 flex flex-col items-center justify-center h-full animate-in zoom-in duration-300">
        <div className="mb-6">
          {score >= 20 ? (
            <div className="p-6 bg-yellow-500/10 rounded-full ring-1 ring-yellow-500/20">
              <Trophy size={64} className="text-yellow-500" />
            </div>
          ) : (
            <div className="p-6 bg-red-500/10 rounded-full ring-1 ring-red-500/20">
              <RefreshCw size={64} className="text-red-500" />
            </div>
          )}
        </div>

        <h2 className="text-4xl md:text-6xl font-black mb-2 italic tracking-tighter uppercase">
          {score >= 20 ? 'Great Job!' : 'Try Again'}
        </h2>
        
        <p className="text-zinc-200 text-[10px] mb-10 uppercase font-black tracking-[0.4em]">
          Final Score: <span className="text-white">{score}</span> / 20
        </p>

        <button className="px-12 py-5 bg-white text-black rounded-2xl font-black uppercase text-xs tracking-[0.2em] active:scale-95 transition-all">
          Play Again
        </button>
      </div>
    )}

    {/* Footer Exit Button */}
    <button 
      onPointerDown={(e) => { e.stopPropagation(); onBack(); }} 
      className="relative z-10 mb-4 text-zinc-700 uppercase text-[9px] font-black tracking-[0.4em] hover:text-red-500 transition-colors py-4"
    >
      [ Go to Menu ]
    </button>
  </div>
)}

      <div className="w-full p-8 flex justify-between z-10 pt-12">
        <StatBox label="Score" value={`${score}/20`} />
        <StatBox label="Stability" value={`${Math.max(0, timeLeft).toFixed(1)}s`} color={timeLeft < 0.6 ? 'text-red-500' : 'text-blue-400'} />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center z-10">
        <div className="p-12 bg-white/5 rounded-full border border-white/10 shadow-2xl">
           <task.dir.icon size={100} className="text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]" />
        </div>
        <p className="text-[10px] text-white/30 font-black uppercase tracking-[0.5em] mt-8 font-mono">
          {task.type === 'NORMAL' ? "OBEY SIGNAL" : "INVERT SIGNAL"}
        </p>
      </div>

      <div className="w-full max-w-sm p-6 grid grid-cols-3 gap-3 mb-12 z-10">
        <div /><ControlButton icon={ChevronUp} onClick={() => handleInput('UP')} /><div />
        <ControlButton icon={ChevronLeft} onClick={() => handleInput('LEFT')} /><div className="opacity-10 flex items-center justify-center text-[8px]">OMORE</div><ControlButton icon={ChevronRight} onClick={() => handleInput('RIGHT')} />
        <div /><ControlButton icon={ChevronDown} onClick={() => handleInput('DOWN')} /><div />
      </div>
    </div>
  );
};

// --- MODULE 2: NEURAL RECALL ---
const NeuralSequence = ({ onBack }) => {
  const [sequence, setSequence] = useState([]);
  const [userPlayback, setUserPlayback] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [gameState, setGameState] = useState('START'); 
  const [level, setLevel] = useState(1);

  const startNextLevel = useCallback((lvl) => {
    const newSeq = Array.from({ length: lvl + 2 }, () => DIRECTIONS[Math.floor(Math.random() * DIRECTIONS.length)]);
    setSequence(newSeq);
    setUserPlayback([]);
    setGameState('WATCH');
    
    let i = 0;
    const interval = setInterval(() => {
      setActiveIndex(i);
      setTimeout(() => setActiveIndex(-1), 350); // Slightly faster flash
      i++;
      if (i >= newSeq.length) {
        clearInterval(interval);
        setTimeout(() => setGameState('PLAY'), 500);
      }
    }, 700); // Slightly faster sequence
  }, []);

  const handleInput = (dirId) => {
    if (gameState !== 'PLAY') return;
    if (dirId === sequence[userPlayback.length].id) {
      const nextPlayback = [...userPlayback, dirId];
      setUserPlayback(nextPlayback);
      if (nextPlayback.length === sequence.length) {
        if (level >= 5) setGameState('WIN');
        else { 
          setLevel(l => l + 1); 
          setTimeout(() => startNextLevel(level + 1), 600); 
        }
      }
    } else {
      setGameState('FAIL');
    }
  };

  return (
    <div className="fixed inset-0 w-full h-[100svh] bg-black text-white flex flex-col overflow-hidden touch-none select-none">
      <div className="absolute inset-0 bg-purple-600/[0.03] pointer-events-none" />

      {/* 1. START SCREEN */}
{/* 1. START SCREEN */}
{/* 1. START SCREEN - NO SCROLL FULLSCREEN */}
{gameState === 'START' && (
  <div className="absolute inset-0 z-[100] bg-[#050505] text-white flex flex-col items-center justify-between p-6 md:p-12 overflow-hidden select-none">
    
    {/* Subtle Background Accent */}
    <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(circle_at_50%_50%,_#a855f715_0%,_transparent_70%)]" />

    {/* TOP: Header */}
    <header className="relative z-10 flex flex-col items-center animate-in fade-in slide-in-from-top-4 duration-500">
      <div className="p-3 bg-purple-500/10 rounded-2xl mb-4">
        <Brain size={32} className="text-purple-500" />
      </div>
      <h1 className="text-4xl md:text-7xl font-black  uppercase tracking-tighter text-center leading-none">
        Pattern <br/> <span className="text-purple-600">Memory</span>
      </h1>
    </header>

    {/* CENTER: Instructions (Flexible Height) */}
    <div className="relative z-10 w-full max-w-xl flex flex-col gap-4 md:gap-6 animate-in fade-in zoom-in duration-700 delay-100">
      <p className="text-[10px] text-zinc-200 uppercase font-black tracking-[0.8em] text-center">How to play</p>
      
      <div className="flex flex-col gap-3">
        {[
          { step: "1", title: "Watch", desc: "The boxes will light up in a specific order." },
          { step: "2", title: "Copy", desc: "Tap the boxes in the exact same order." },
          { step: "3", title: "Survive", desc: "One wrong tap and the session ends." }
        ].map((item) => (
          <div key={item.step} className="flex items-center gap-4 p-4 bg-zinc-900/40 border border-white/[0.03] rounded-2xl hover:bg-zinc-900/60 transition-colors">
            <div className="w-8 h-8 shrink-0 bg-purple-600 rounded-lg flex items-center justify-center text-xs font-black shadow-[0_0_15px_rgba(147,51,234,0.3)]">
              {item.step}
            </div>
            <div className="text-left">
              <h3 className="text-sm font-black uppercase tracking-tight leading-none mb-1">{item.title}</h3>
              <p className="text-[10px] md:text-xs text-zinc-200 font-medium">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* BOTTOM: Action Buttons */}
    <footer className="relative z-10 w-full max-w-xs flex flex-col items-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
      <button 
        onClick={() => startNextLevel(1)} 
        className="w-full py-5 bg-purple-600 hover:bg-purple-500 text-white rounded-2xl font-black uppercase text-xs tracking-[0.2em] active:scale-95 transition-all shadow-[0_15px_30px_rgba(147,51,234,0.2)]"
      >
        Start Training
      </button>
      
      <button 
        onClick={onBack} 
        className="text-zinc-600 hover:text-white text-[9px] uppercase font-black tracking-[0.4em] transition-colors py-2"
      >
        [ Return ]
      </button>
    </footer>

  </div>
)}

      {/* 2. COMPACT HUD */}
      <div className="relative z-10 w-full px-5 pt-8 flex justify-between items-center">
        <div className="bg-zinc-900/50 p-3 rounded-xl border border-white/5">
          <p className="text-[7px] text-zinc-200 font-black uppercase tracking-widest mb-0.5">Level</p>
          <p className="text-lg font-black italic text-purple-400 leading-none">{level}/5</p>
        </div>
        <div className="bg-zinc-900/50 p-3 rounded-xl border border-white/5 text-right">
          <p className="text-[7px] text-zinc-200 font-black uppercase tracking-widest mb-0.5">Progress</p>
          <p className="text-lg font-black italic leading-none">{userPlayback.length}/{sequence.length}</p>
        </div>
      </div>

      {/* 3. SCALED DOWN DISPLAY AREA */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center">
        <div className={`w-32 h-32 xs:w-36 xs:h-36 rounded-full border border-white/5 bg-zinc-900/20 flex items-center justify-center relative transition-all duration-300 ${activeIndex !== -1 ? 'scale-105 border-purple-500/30 bg-purple-500/10' : ''}`}>
          
          <div className={`transition-all duration-200 ${activeIndex !== -1 ? 'scale-110 opacity-100' : 'opacity-20'}`}>
            {activeIndex !== -1 ? (
              React.createElement(sequence[activeIndex].icon, { size: 50, className: "text-purple-400" })
            ) : (
              <Brain size={50} className="text-zinc-800" />
            )}
          </div>

          <div className="absolute -bottom-10 left-0 right-0 text-center">
             <p className={`text-[8px] font-black uppercase tracking-[0.3em] ${gameState === 'WATCH' ? 'text-zinc-200 animate-pulse' : 'text-purple-400'}`}>
                {gameState === 'WATCH' ? 'Memorize' : gameState === 'PLAY' ? 'Your Turn' : ''}
             </p>
          </div>
        </div>

        {/* 4. RESULTS OVERLAY */}
        {(gameState === 'WIN' || gameState === 'FAIL') && (
          <div className="absolute inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center animate-in fade-in">
            <h2 className="text-2xl font-black italic mb-1 tracking-tighter">
              {gameState === 'WIN' ? 'MASTERED' : 'FAILED'}
            </h2>
            <p className="text-zinc-200 text-[10px] font-bold mb-6">
              {gameState === 'WIN' ? 'Memory capacity expanded.' : 'Sequence link lost.'}
            </p>
            <button 
              onClick={() => { setLevel(1); setGameState('START'); }} 
              className="px-8 py-3 bg-white text-black rounded-xl font-black uppercase text-[10px] tracking-widest active:scale-95"
            >
              Try Again
            </button>
          </div>
        )}
      </div>

      {/* 5. INPUT CONTROLS - Tightened spacing */}
      <div className="relative z-10 w-full max-w-[280px] mx-auto px-4 pb-6 grid grid-cols-3 gap-2">
        <div />
        <ControlButton icon={ChevronUp} active={gameState === 'PLAY'} onClick={() => handleInput('UP')} size="h-16" />
        <div />

        <ControlButton icon={ChevronLeft} active={gameState === 'PLAY'} onClick={() => handleInput('LEFT')} size="h-16" />
        <div className="flex items-center justify-center">
          <div className={`w-1.5 h-1.5 rounded-full ${gameState === 'PLAY' ? 'bg-purple-500 shadow-[0_0_8px_#a855f7]' : 'bg-zinc-800'}`} />
        </div>
        <ControlButton icon={ChevronRight} active={gameState === 'PLAY'} onClick={() => handleInput('RIGHT')} size="h-16" />

        <div />
        <ControlButton icon={ChevronDown} active={gameState === 'PLAY'} onClick={() => handleInput('DOWN')} size="h-16" />
        <div />
      </div>

      <button 
        onClick={onBack} 
        className="relative z-10 pb-4 text-zinc-700 uppercase text-[8px] tracking-[0.3em] font-black"
      >
        Quit Lab
      </button>
    </div>
  );
};

// --- HELPER COMPONENTS ---
const StatBox = ({ label, value, color = "text-white" }) => (
  <div className="bg-zinc-900/60 p-5 rounded-3xl border border-white/10 backdrop-blur-xl min-w-[110px]">
    <p className="text-[8px] text-zinc-200 font-black uppercase tracking-widest mb-1">{label}</p>
    <p className={`text-2xl font-black italic tracking-tighter ${color}`}>{value}</p>
  </div>
);

const ControlButton = ({ icon: Icon, onClick, active = true }) => (
  <button 
    onPointerDown={(e) => { e.preventDefault(); if(active) onClick(); }}
    className={`h-20 rounded-[2rem] flex items-center justify-center transition-all shadow-2xl active:scale-90
      ${active ? 'bg-zinc-900/80 border border-white/10 text-white active:bg-white active:text-black' : 'bg-zinc-950 text-zinc-800 border border-white/5 cursor-not-allowed'}`}
  >
    <Icon size={32} />
  </button>
);

const MenuButton = ({ title, desc, icon: Icon, onClick, color }) => (
  <button 
    onClick={onClick}
    className={`w-full p-6 bg-zinc-900/40 border ${color} rounded-[2.5rem] flex items-center gap-6 hover:bg-white hover:text-black transition-all group backdrop-blur-sm shadow-xl active:scale-95`}
  >
    <div className="p-4 bg-white/5 rounded-[1.5rem] group-hover:bg-black/5">
      <Icon size={28} />
    </div>
    <div className="text-left">
      <p className="font-black italic uppercase tracking-tighter text-xl leading-none mb-1">{title}</p>
      <p className="text-[9px] uppercase tracking-widest opacity-40 font-bold">{desc}</p>
    </div>
  </button>
);

export default NeuralManager;